import { useForm as useReactHookForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { useTemplate } from '../../hooks/useTemplate';
import { useInstantiateTemplate } from '../../hooks/useTemplateMutations';
import { FormResponseView } from '../FormRenderer/FormResponseView';
import { buildSubmissionSchema } from '../../schemas';

interface TemplatePreviewDialogProps {
  templateId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Footer secondary-button label — defaults to a plain "Cerrar". Pass the gallery's "Volver a la galería" when opened from that flow. */
  backLabel?: string;
  /** Called right before navigating away on a successful "Usar esta plantilla" — e.g. to also close a parent gallery dialog. */
  onUsed?: () => void;
}

/**
 * Template preview — reuses `FormResponseView` (the same component `FormPreviewDialog`
 * uses) so both previews are visually identical. Submit is hidden in favor of this
 * dialog's own footer ("Cerrar" / "Usar esta plantilla").
 */
export function TemplatePreviewDialog({
  templateId,
  open,
  onOpenChange,
  backLabel,
  onUsed,
}: TemplatePreviewDialogProps) {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const { template, isLoading, isError } = useTemplate(open ? templateId : '');
  const instantiate = useInstantiateTemplate(templateId);

  const sortedFields = template?.fields.slice().sort((a, b) => a.order - b.order) ?? [];
  const schema = buildSubmissionSchema(sortedFields, template?.emailRequired ?? false);

  const rhfForm = useReactHookForm({
    resolver: zodResolver(schema),
    defaultValues: { email: '', fields: {} },
  });

  async function handleUse() {
    try {
      const { data: created } = await instantiate.mutateAsync();
      onOpenChange(false);
      onUsed?.();
      navigate(`/${orgId}/forms/${created.id}`);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full h-full max-w-none rounded-none overflow-y-auto md:h-[85vh] md:max-w-2xl md:rounded-lg"
        data-testid="forms-template-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('preview.dialogTitle', { title: template?.title ?? '' })}</DialogTitle>
        </DialogHeader>

        <FormResponseView
          testIdPrefix="forms-template-preview"
          isLoading={isLoading}
          isError={isError}
          form={
            template
              ? {
                  title: template.title,
                  description: template.description,
                  status: 'Shared',
                  emailRequired: template.emailRequired,
                  fields: template.fields,
                }
              : undefined
          }
          submitted={false}
          rhfForm={rhfForm}
          onSubmit={(e) => e.preventDefault()}
          isSubmitting={false}
          previewMode
          embedded
          hideSubmit
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {backLabel ?? t('templates.preview.close')}
          </Button>
          <Button type="button" onClick={handleUse} disabled={instantiate.isPending || isLoading}>
            {t('templates.gallery.useTemplate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
